import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { QuickTaskIcon } from './QuickTaskIcon';
import { brandColors } from '@/design';

type QuickPlanInputMode = 'text' | 'voice';

interface QuickPlanModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: { instruction: string; inputMode: QuickPlanInputMode }) => Promise<void>;
}

interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly 0: { transcript: string };
}

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const maybeWindow = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return maybeWindow.SpeechRecognition ?? maybeWindow.webkitSpeechRecognition ?? null;
}

function formatError(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return 'Could not create a quick plan right now.';
}

function mergeTranscript(existing: string, addition: string): string {
  const next = addition.trim();
  if (!next) return existing;
  if (!existing.trim()) return next;
  return `${existing.trim()} ${next}`;
}

export function QuickPlanModal({ open, onClose, onCreate }: QuickPlanModalProps) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const interimTranscriptRef = useRef('');
  const [inputMode, setInputMode] = useState<QuickPlanInputMode>('text');
  const [instruction, setInstruction] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const speechRecognitionCtor = useMemo(() => getSpeechRecognitionCtor(), []);
  const speechSupported = Boolean(speechRecognitionCtor);

  useEffect(() => {
    if (open) return;
    setInputMode('text');
    setInstruction('');
    setInterimTranscript('');
    interimTranscriptRef.current = '';
    setError(null);
    setSubmitting(false);
    setIsListening(false);
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      interimTranscriptRef.current = '';
      if (silenceTimerRef.current !== null) {
        window.clearTimeout(silenceTimerRef.current);
      }
    };
  }, []);

  const silenceTimerRef = useRef<number | null>(null);
  const lastSpeechAtRef = useRef<number>(0);
  const SILENCE_TIMEOUT_MS = 8000;

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const resetSilenceTimer = () => {
    clearSilenceTimer();
    lastSpeechAtRef.current = Date.now();
    silenceTimerRef.current = window.setTimeout(() => {
      recognitionRef.current?.stop();
    }, SILENCE_TIMEOUT_MS);
  };

  const flushInterimTranscript = () => {
    const pendingInterim = interimTranscriptRef.current.trim();
    if (!pendingInterim) return;
    setInstruction((prev) => mergeTranscript(prev, pendingInterim));
    interimTranscriptRef.current = '';
  };

  const toggleVoiceCapture = () => {
    setError(null);

    if (!speechRecognitionCtor) {
      setError('Voice input is not supported in this browser.');
      return;
    }

    if (isListening) {
      clearSilenceTimer();
      recognitionRef.current?.stop();
      return;
    }

    setInputMode('voice');

    const recognition = new speechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? '';
        if (result?.isFinal) finalText = mergeTranscript(finalText, transcript);
        else interim = mergeTranscript(interim, transcript);
      }
      if (finalText) {
        setInstruction((prev) => mergeTranscript(prev, finalText));
      }
      setInterimTranscript(interim);
      interimTranscriptRef.current = interim;
      resetSilenceTimer();
    };
    recognition.onerror = () => {
      clearSilenceTimer();
      flushInterimTranscript();
      setError('Could not capture voice input. Please try again or type your instruction.');
      setIsListening(false);
      setInterimTranscript('');
      interimTranscriptRef.current = '';
      recognitionRef.current = null;
    };
    recognition.onend = () => {
      clearSilenceTimer();
      flushInterimTranscript();
      setIsListening(false);
      setInterimTranscript('');
      interimTranscriptRef.current = '';
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
      setInterimTranscript('');
      interimTranscriptRef.current = '';
      resetSilenceTimer();
    } catch {
      setError('Microphone access is unavailable right now.');
      setIsListening(false);
      interimTranscriptRef.current = '';
      recognitionRef.current = null;
    }
  };

  const submit = async () => {
    const mergedInstruction = mergeTranscript(instruction, interimTranscriptRef.current);
    if (isListening) {
      clearSilenceTimer();
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      setIsListening(false);
      setInterimTranscript('');
      interimTranscriptRef.current = '';
    }

    const trimmed = mergedInstruction.trim();
    if (!trimmed || submitting) return;
    setInstruction(mergedInstruction);
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({ instruction: trimmed, inputMode });
      onClose();
    } catch (submitError) {
      setError(formatError(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-lg rounded-2xl p-6 flex flex-col gap-4"
        style={{
          background: 'var(--color-surface-elevated, var(--color-background))',
          border: `2px solid ${brandColors.teal}88`,
          boxShadow: `0 0 0 1px ${brandColors.teal}22, 0 24px 48px rgba(0, 0, 0, 0.35), 0 8px 16px rgba(0, 0, 0, 0.2)`,
        }}
      >
        <div>
          <h3 className="font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
            <QuickTaskIcon size={24} />
            Quick Plan
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            Describe what you want at a high level. Flank will generate one or more tasks.
          </p>
        </div>

        <textarea
          value={instruction}
          onChange={(event) => setInstruction(event.target.value)}
          placeholder="Example: Plan my prep for tomorrow's client call."
          className="w-full min-h-[120px] rounded-xl border px-3 py-2.5 text-sm bg-[var(--color-surface)] text-[var(--color-text-primary)] resize-y placeholder:text-[var(--color-text-muted)]"
          style={{ borderColor: 'var(--color-border)' }}
          disabled={submitting}
          autoFocus
        />

        {interimTranscript && (
          <p className="text-xs text-[var(--color-text-muted)] -mt-2">
            Listening: {interimTranscript}
          </p>
        )}

        {error && (
          <div
            className="rounded-lg px-3 py-2 text-sm"
            style={{ background: 'var(--color-error-subtle)', color: 'var(--color-error)' }}
          >
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (isListening) recognitionRef.current?.stop();
              onClose();
            }}
            className="flank-btn flank-btn-secondary flex-1 text-sm"
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={submitting || instruction.trim().length === 0}
            className="flank-btn flank-btn-primary flex-1 text-sm inline-flex items-center justify-center whitespace-nowrap"
          >
            {submitting
              ? <Loader2 size={16} className="animate-spin" aria-label="Creating plan" />
              : 'Create plan'}
          </button>

          {speechSupported && (
            <div className="shrink-0 flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={toggleVoiceCapture}
                disabled={submitting}
                title={isListening ? 'Tap to finish' : 'Use voice input'}
                className="w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors"
                style={{
                  borderColor: isListening ? 'var(--color-error)' : `${brandColors.teal}88`,
                  background: isListening ? 'var(--color-error-subtle)' : `${brandColors.teal}1A`,
                  color: isListening ? 'var(--color-error)' : brandColors.teal,
                }}
              >
                {isListening ? <MicOff size={18} /> : <Mic size={18} />}
              </button>
              {isListening && (
                <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                  Tap to finish
                </span>
              )}
              {!isListening && inputMode === 'voice' && (
                <span className="text-[10px] text-[var(--color-text-muted)] whitespace-nowrap">
                  {instruction.trim().length > 0 ? 'Tap to continue' : 'Tap to start'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
